/**
 * Canonical content model for MyAE feature pages.
 *
 * Every feature MUST answer, in calm-academic voice:
 *   - What it does
 *   - How it works
 *   - What MyAE considers
 *   - What you receive
 *   - Limitations (honest)
 *   - Mini FAQ (5 questions)
 * Plus outbound internal links to sibling features + related use cases so
 * the pages form a genuine knowledge graph rather than isolated SEO pages.
 */
export const FEATURES = [
  {
    slug: "grade-prediction",
    title: "Grade Prediction",
    intro:
      "Grade Prediction gives you a realistic, rubric-aware indication of how your current draft is likely to be marked at your university. It is designed to help you decide what to improve before submission — not to replace your lecturer's decision.",
    what_it_does:
      "MyAE's Grade Prediction reads your assignment brief, your rubric or marking criteria and your submission, then returns a predicted grade range — for example \"63–68% (2:1)\" — along with a transparent confidence score and an explanation of how that range was reached. The output is calibrated: the underlying model produces a structured academic assessment, and MyAE's calibration engine converts that assessment into the final range using historical benchmarks and confirmed lecturer marks.",
    how_it_works: [
      "Extract the brief, rubric and submission text from your uploaded files (PDF, DOCX or pasted text).",
      "Interpret the brief and identify the specific learning outcomes and instructions it signals.",
      "Score each rubric criterion against the submission with short evidence quotes taken directly from your work.",
      "Assess referencing style, citation integrity and academic level (Y1 to PhD).",
      "Produce a structured academic assessment which the calibration engine then converts into the predicted grade range.",
    ],
    considered: [
      "The exact wording of your assignment brief and learning outcomes.",
      "Every rubric criterion, its weight if provided, and the descriptors for each band.",
      "Structure, argumentation, use of evidence, referencing and academic voice.",
      "Any assessment-specific requirements (word count, referencing style, tone).",
    ],
    what_you_receive: [
      "A predicted grade range with a transparent confidence score (0–100%).",
      "Criterion-by-criterion scores with short evidence quotes from your work.",
      "Strengths and weaknesses summarised in a short lecturer-style narrative.",
      "A submission-readiness signal indicating whether the draft is ready, needs revision or is not ready.",
    ],
    limitations: [
      "Grade Prediction is an indication, not an official grade. Your institution's decision always overrides MyAE.",
      "Grading conventions vary by university, faculty and even module — the prediction is calibrated against a broad benchmark, not your specific marker.",
      "The prediction reflects the draft you upload. If you meaningfully revise the essay, re-run it to see the effect.",
    ],
    faq: [
      { q: "Is the predicted grade guaranteed?", a: "No. It is an indicative range based on your rubric and draft. Your university's marker makes the final decision." },
      { q: "Why a range instead of a single number?", a: "Academic marking is not a single deterministic value. A range communicates the realistic band your work sits in, given uncertainty about how a specific marker will read it." },
      { q: "How does MyAE decide the range?", a: "The AI model produces a structured academic assessment (criterion scores, quality signals, confidence). A separate calibration engine converts that into the final grade range using historical benchmarks and confirmed lecturer grades — so the model does not pick the grade on its own." },
      { q: "Can I improve my predicted grade?", a: "Yes. Address the weaknesses MyAE lists, tighten your rubric alignment, then re-analyse to see the updated range." },
      { q: "Does MyAE work for all UK universities?", a: "Yes — the prediction is rubric-driven, so it adapts to your specific brief and marking criteria rather than assuming a single grading scale." },
    ],
    related: ["assignment-analysis", "essay-feedback", "assignment-brief-analysis"],
  },
  {
    slug: "assignment-analysis",
    title: "Assignment Analysis",
    intro:
      "Assignment Analysis is the deep pass MyAE performs on your submission — the diagnostic layer under the predicted grade. It maps your essay against your brief, criterion by criterion, and shows you exactly where the marks are and where they are being lost.",
    what_it_does:
      "MyAE takes your uploaded submission and analyses it in structured layers: brief interpretation, rubric alignment, criterion-by-criterion scoring, referencing integrity, and overall academic judgement. Each finding is anchored to short verbatim quotes from your own work — so the feedback is traceable and easy to act on.",
    how_it_works: [
      "Parse the brief and the rubric to build a criterion map.",
      "Walk the submission and evidence each criterion with direct quotes.",
      "Score each criterion against the rubric descriptors, band by band.",
      "Detect missing rubric elements — requirements the brief lists but your submission does not address.",
      "Return a full academic assessment (schema-defined) plus criterion scores.",
    ],
    considered: [
      "Every rubric criterion and its weight if provided.",
      "Direct quotes from the submission body (never paraphrased for scoring).",
      "Structural markers: introduction, argument development, evidence, critical analysis, conclusion.",
      "Referencing and citation integrity as a stand-alone criterion.",
    ],
    what_you_receive: [
      "A criterion-by-criterion breakdown with band descriptors and evidence quotes.",
      "A list of missing rubric elements that could cost marks.",
      "A prioritised list of top strengths and top weaknesses.",
      "A structured JSON assessment that feeds Grade Prediction and Essay Feedback.",
    ],
    limitations: [
      "Assignment Analysis reads what is on the page — it cannot infer content you intended but did not write.",
      "Rubric interpretation is only as reliable as the rubric you upload. Vague or ambiguous rubrics produce broader scoring bands.",
      "For scanned PDFs, text is OCR'd via Google Cloud Vision; unusual formatting can affect extraction quality.",
    ],
    faq: [
      { q: "How is this different from generic AI feedback?", a: "Generic AI reads your essay and comments on the writing. MyAE reads your essay against your specific brief and rubric — the feedback is anchored to your assessment, not a general standard." },
      { q: "Can I trust the quotes MyAE pulls?", a: "The quotes are extracted verbatim from your own submission text. If a quote does not match your document, it is treated as a hallucination flag and surfaced to admin." },
      { q: "What if my rubric is written in a language other than English?", a: "MyAE currently reads English-language rubrics best. Non-English rubrics are supported but the confidence score will typically be lower." },
      { q: "Does Assignment Analysis change my draft?", a: "No. It only analyses the draft you upload. You always keep the original file." },
      { q: "Is my submission stored?", a: "Yes — securely, in the EU, and only used to generate your reports. See our Privacy Policy for retention details." },
    ],
    related: ["grade-prediction", "essay-feedback", "assignment-brief-analysis"],
  },
  {
    slug: "essay-feedback",
    title: "Essay Feedback",
    intro:
      "Essay Feedback is the student-facing side of MyAE's assessment: a lecturer-style narrative that tells you what is working, what is weak and what you can do about it — in the tone of a supportive academic reader, not a grade-obsessed critic.",
    what_it_does:
      "MyAE turns the structured academic assessment into a short, readable feedback report. You receive prioritised strengths and weaknesses, a lecturer-style narrative that explains your predicted band, and a checklist of specific improvements ordered by likely impact on the mark.",
    how_it_works: [
      "Consume the structured academic assessment produced by Assignment Analysis.",
      "Rank each identified issue by its likely impact on the criterion score it belongs to.",
      "Generate a short (~150 word) lecturer-style narrative that reads like a real academic tutor's comment.",
      "Produce a prioritised improvement checklist — the changes most likely to move your predicted band upward.",
    ],
    considered: [
      "The rubric-mapped criterion scores from Assignment Analysis.",
      "Structural signals: argument coherence, evidence integration, critical depth, referencing.",
      "Academic level (undergraduate Y1 to PhD) so the tone of feedback matches the audience.",
      "Assessment type — essay, report, dissertation chapter, case study, reflective piece.",
    ],
    what_you_receive: [
      "A prioritised list of strengths and weaknesses.",
      "A lecturer-style narrative explaining your predicted mark.",
      "A ranked improvement checklist — highest-impact changes at the top.",
      "Optional deeper dives per criterion when you want more detail.",
    ],
    limitations: [
      "Feedback quality mirrors the clarity of your rubric — vague criteria produce broader, less specific comments.",
      "Essay Feedback is a study aid, not a proofreading service. It focuses on academic quality, not surface-level copy-editing.",
      "MyAE does not rewrite your essay. Its role is to guide, not to author.",
    ],
    faq: [
      { q: "Will MyAE rewrite my essay?", a: "No — MyAE gives you feedback, not a ghost-written draft. Rewriting your essay yourself, informed by the feedback, is what improves your writing." },
      { q: "Is the tone harsh?", a: "No — Essay Feedback is written in the tone of a supportive academic tutor, not a marker looking for reasons to deduct." },
      { q: "How many improvements will I get?", a: "Typically five to eight prioritised improvements per report, ordered by expected impact on the predicted grade." },
      { q: "Can I re-run after making changes?", a: "Yes. Re-analysing after edits is exactly the intended workflow — the improvement checklist should shrink as you address items." },
      { q: "Does Essay Feedback replace my tutor?", a: "No. It is a study aid to help you improve between tutor sessions, not a substitute for them." },
    ],
    related: ["grade-prediction", "assignment-analysis", "submission-intelligence"],
  },
  {
    slug: "submission-intelligence",
    title: "Submission Intelligence",
    intro:
      "Submission Intelligence is the final safety pass before you hand in. It combines multiple independent signals — web similarity, source and evidence quality, referencing checks, AI-writing indicators and (coming soon) AI Provenance — into a single, non-accusatory readout that helps you spot issues while there is still time to fix them.",
    what_it_does:
      "MyAE runs your submission through several independent lenses and reports what it finds. Nothing here is presented as an accusation. The goal is to give you enough context to make a confident submission decision — and to flag anything you should double-check with your tutor.",
    how_it_works: [
      "Query Brave Search for close matches to short excerpts of your text.",
      "Query Exa AI for relevant academic evidence around your key claims.",
      "Check every citation against its bibliography entry and vice versa.",
      "Analyse linguistic AI-writing indicators (style consistency, generic AI constructions, abrupt shifts).",
      "Once official provider watermark detection is available and validated, layer in AI Provenance signals — starting with Anthropic Claude.",
      "Combine the signals into a plain-English summary with recommended action.",
    ],
    considered: [
      "Short excerpts of your text — never your whole essay is shared externally.",
      "The bibliography entries and in-text citations you supplied.",
      "The linguistic fingerprint of your writing compared with generic AI text patterns.",
      "Provider-official provenance signals where an official detection API is available.",
    ],
    what_you_receive: [
      "A summary of web similarity hits with source links you can review.",
      "Suggested additional evidence to strengthen key claims.",
      "A referencing integrity check — orphan citations, orphan references, style inconsistencies.",
      "Linguistic AI-writing indicators presented as informational signals, not accusations.",
      "An AI Provenance card — currently \"Coming Soon\", pending official provider APIs.",
    ],
    limitations: [
      "AI-writing indicators are not proof of AI authorship. They highlight patterns; interpretation is up to you and your institution.",
      "Web similarity draws on external search engines. Coverage is not exhaustive.",
      "AI Provenance detection is not currently active for students — see the AI Provenance section on the Submission Intelligence report card for status.",
    ],
    faq: [
      { q: "Is Submission Intelligence a plagiarism detector?", a: "No. It is a multi-signal readout that includes similarity checks alongside referencing, evidence and AI-writing signals. Plagiarism is your institution's determination — MyAE only provides indicators." },
      { q: "Does MyAE claim my essay was written by AI?", a: "No — never. MyAE surfaces indicators. It does not accuse. Any provenance signal we ship will be provider-official and worded to make clear that a detected signal does not establish authorship or academic misconduct." },
      { q: "Are my whole essays shared with Brave or Exa?", a: "No. Only short excerpts (up to 300 characters) are sent externally for similarity look-up. Your full essay stays with MyAE." },
      { q: "When will AI Provenance detection be live?", a: "Once an official detection mechanism (starting with Anthropic Claude) is available, integrated and validated. Until then it remains a Coming Soon informational card." },
      { q: "Will Submission Intelligence flag me as a cheat?", a: "No. Every signal is presented as informational context to help you decide what to check before submission. It never labels students." },
    ],
    related: ["essay-feedback", "assignment-analysis", "grade-prediction"],
  },
  {
    slug: "assignment-brief-analysis",
    title: "Assignment Brief Analysis",
    intro:
      "Assignment Brief Analysis unpacks the brief itself — the document your entire grade is going to be judged against. It surfaces the specific tasks, learning outcomes and requirements the marker will be looking for, so you can plan and write with clarity from day one.",
    what_it_does:
      "MyAE reads your assignment brief and produces a plain-English breakdown: what the brief is asking for in one sentence, the learning outcomes it signals, the specific instructions ('critically evaluate', 'compare and contrast'…), and any implicit requirements around evidence, structure or referencing.",
    how_it_works: [
      "Extract the text of the brief from your upload.",
      "Identify the task type (essay, report, reflective, case study…) and academic level.",
      "Parse learning outcomes into a normalised list.",
      "Pull direct instructional verbs and turn them into concrete writing implications.",
      "Estimate an implicit rubric where one is not provided, based on the task type and level.",
    ],
    considered: [
      "The full text of the brief, including footnotes and appendix instructions.",
      "The task-type conventions (essay vs report vs reflective vs dissertation).",
      "The academic level implied by the brief's vocabulary.",
      "Any word-count, referencing style, or submission requirements stated in the brief.",
    ],
    what_you_receive: [
      "A one-sentence summary of what the brief is really asking for.",
      "A clean list of learning outcomes and key instructions.",
      "A coverage checklist you can tick as you draft.",
      "Notes on brief ambiguity — points to clarify with your tutor.",
    ],
    limitations: [
      "Brief Analysis is only as complete as the brief you upload — if requirements live in an accompanying handbook, upload that too.",
      "MyAE flags ambiguity but cannot resolve it: some questions are for your tutor to answer.",
      "Implicit rubric estimation is a best-effort scaffold, not a replacement for your actual rubric.",
    ],
    faq: [
      { q: "What if I don't have a rubric?", a: "MyAE will still analyse the brief and estimate an implicit rubric based on the task type and academic level. If your tutor later shares the rubric, re-run for a sharper prediction." },
      { q: "Does MyAE tell me what to write?", a: "No — it clarifies what the brief is asking for. Writing the essay is your work." },
      { q: "Can I use this before I start writing?", a: "Yes — Assignment Brief Analysis is designed to be your day-one step, before you draft a single paragraph." },
      { q: "What formats are supported?", a: "PDF, DOCX and pasted plain text. Scanned briefs are OCR'd." },
      { q: "Is my brief shared with anyone?", a: "No — briefs are stored securely in the EU and only processed to generate your reports." },
    ],
    related: ["grade-prediction", "assignment-analysis", "essay-feedback"],
  },
];

export const FEATURE_MAP = Object.fromEntries(FEATURES.map((f) => [f.slug, f]));
